/**
 * メインレイアウト - Nexus Core (AIRGROW)
 * - 左ツールバー (固定 w-16、開閉なし)
 * - トップバー (高さ h-16): AIRGROW ロゴ + ページ名 + ユーザーメニュー
 */
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { userApi } from '../services/droneOpsApi';

const NAV_ITEMS = [
  { path: '/', icon: 'calendar_month', label: 'フライト予約', description: '機体予約・フライト管理' },
  { path: '/dips', icon: 'dataset', label: '登録機体一覧', description: 'DIPS2.0 (国土交通省ドローン情報基盤システム) 連携機体一覧' },
];

const ROLE_LABELS = {
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧者',
};

function MaterialIcon({ name, className = '', filled = false, weight, style = {} }) {
  const fillSetting = filled ? "'FILL' 1" : "'FILL' 0";
  const wghtSetting = weight ? `, 'wght' ${weight}` : '';
  return (
    <span
      className={`material-symbols-outlined ${className}`}
      style={{ fontVariationSettings: `${fillSetting}${wghtSetting}`, ...style }}
    >
      {name}
    </span>
  );
}

// 選択中: アイコンを 2 層重ねて「線=Nexus Primary / 内部=Nexus Blue Soft」を表現
const TOOLBAR_ICON_WGHT = 200;

// ツールバー項目共通の吹き出しツールチップ。左側にアロー (枠 + 塗りの 2 層)
// アローを長め (border-r 12px) にし、ツールバー右端に少し被さる位置に表示
function ToolbarTooltip({ children }) {
  return (
    <span
      role="tooltip"
      className="
        pointer-events-none absolute left-full -ml-0.5 px-3 py-2 rounded-md
        bg-surface-container-lowest text-text-primary text-sm whitespace-nowrap
        opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-md
        border border-border-light
        before:content-[''] before:absolute before:top-1/2 before:-translate-y-1/2 before:left-[-13px]
        before:border-y-[8px] before:border-y-transparent before:border-r-[12px] before:border-r-border-light
        after:content-[''] after:absolute after:top-1/2 after:-translate-y-1/2 after:left-[-11px]
        after:border-y-[7px] after:border-y-transparent after:border-r-[11px] after:border-r-surface-container-lowest
      "
    >
      {children}
    </span>
  );
}
function ActiveDuotoneIcon({ name }) {
  return (
    <span className="relative inline-flex w-[36px] h-[36px] items-center justify-center leading-none">
      <span
        className="material-symbols-outlined absolute inset-0 flex items-center justify-center text-nexus-blue-soft text-[36px]"
        style={{ fontVariationSettings: `'FILL' 1, 'wght' ${TOOLBAR_ICON_WGHT}` }}
      >
        {name}
      </span>
      <span
        className="material-symbols-outlined absolute inset-0 flex items-center justify-center text-nexus-primary text-[36px]"
        style={{ fontVariationSettings: `'FILL' 0, 'wght' ${TOOLBAR_ICON_WGHT}` }}
      >
        {name}
      </span>
    </span>
  );
}

function ToolbarItem({ to, icon, label }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `group relative flex items-center justify-center h-12 mx-0 rounded-lg transition-colors ${
          isActive ? '' : 'text-outline hover:bg-light-neutral hover:text-text-primary'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? (
            <ActiveDuotoneIcon name={icon} />
          ) : (
            <MaterialIcon name={icon} weight={TOOLBAR_ICON_WGHT} className="text-[36px]" />
          )}
          <ToolbarTooltip>{label}</ToolbarTooltip>
        </>
      )}
    </NavLink>
  );
}

// ナビ以外（設定等）のツールバー項目。NavLink ではなく通常 button
function ToolbarButton({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex items-center justify-center h-12 mx-0 w-full rounded-lg text-outline hover:bg-light-neutral hover:text-text-primary transition-colors"
    >
      <MaterialIcon name={icon} weight={TOOLBAR_ICON_WGHT} className="text-[36px]" />
      <ToolbarTooltip>{label}</ToolbarTooltip>
    </button>
  );
}

function UserMenu({ user, profile, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const initial = (user?.email || '?').charAt(0).toUpperCase();
  const displayName =
    profile?.displayName || (user?.email ? user.email.split('@')[0] : '');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 group"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {displayName && (
          <span className="text-base font-medium text-text-primary max-w-[180px] truncate">
            {displayName}
          </span>
        )}
        <span className="w-9 h-9 rounded-full bg-nexus-blue-soft text-nexus-primary font-semibold text-sm flex items-center justify-center group-hover:ring-2 group-hover:ring-nexus-primary/30 transition-all">
          {initial}
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="nexus-menu-expand absolute right-0 mt-2 w-64 bg-surface-container-lowest border border-border-light rounded-lg shadow-lg overflow-hidden z-50"
        >
          <div className="px-4 py-3">
            <div className="text-sm font-medium text-text-primary truncate">{user?.email || '-'}</div>
            <div className="text-xs text-text-secondary mt-0.5">
              {profile ? ROLE_LABELS[profile.role] || profile.role || '-' : '...'}
            </div>
          </div>
          <div className="border-t border-border-light" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-text-primary hover:bg-light-neutral transition-colors"
          >
            <MaterialIcon name="logout" className="text-[18px] text-outline" />
            ログアウト
          </button>
        </div>
      )}
    </div>
  );
}

// バッジ用 variant → Tailwind class マッピング
const HEADER_BADGE_VARIANTS = {
  success: 'bg-emerald-50 text-emerald-700',
  danger: 'bg-red-50 text-red-700',
  warning: 'bg-amber-50 text-amber-700',
  info: 'bg-nexus-blue-soft text-nexus-primary',
  default: 'bg-light-neutral text-text-secondary',
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [headerBadge, setHeaderBadge] = useState(null); // { variant, label } | null

  useEffect(() => {
    userApi.me().then(setProfile).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentNav = NAV_ITEMS.find((item) =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
  );
  const pageTitle = currentNav?.label || '';
  const pageDescription = currentNav?.description || '';

  return (
    <div className="font-inter min-h-screen bg-light-neutral text-text-primary relative">
      {/* 背景演出 (ログイン画面と統一) */}
      <div className="fixed inset-0 geometric-bg z-0"></div>
      {/* 左ツールバー (浮かせる、最上位 z-50) */}
      <aside className="fixed left-3 top-3 bottom-3 w-16 z-50 flex flex-col items-stretch py-4 bg-surface-container-lowest border border-border-light rounded-xl shadow-lg">
        {/* AIRGROW ロゴ (Y方向グラデ inverse-primary → nexus-primary) */}
        <div className="flex items-center justify-center mb-6">
          <div className="w-10 h-10 bg-gradient-to-b from-inverse-primary to-nexus-primary rounded-lg flex items-center justify-center shadow-md shadow-nexus-primary/20">
            <MaterialIcon name="rocket_launch" filled className="text-white text-[22px]" />
          </div>
        </div>

        {/* ナビゲーション */}
        <nav className="flex flex-col gap-2" aria-label="メインナビゲーション">
          {NAV_ITEMS.map((item) => (
            <ToolbarItem key={item.path} to={item.path} icon={item.icon} label={item.label} />
          ))}
        </nav>

        {/* セパレータ + 設定メニュー (下端) */}
        <div className="mt-auto">
          <div className="border-t border-border-light mx-3 my-2" />
          <ToolbarButton icon="settings" label="設定" onClick={() => {}} />
        </div>
      </aside>

      {/* タイトルバー (浮かせる、ツールバーの右隣から開始) */}
      <header className="fixed top-3 right-3 left-[88px] h-16 z-40 flex justify-between items-center px-6 bg-surface-container-lowest border border-border-light rounded-xl shadow-lg">
        <div className="flex items-center gap-4">
          <h1 className="text-[20px] leading-[28px] font-bold tracking-tight bg-gradient-to-b from-inverse-primary to-nexus-primary bg-clip-text text-transparent">
            AIRGROW
          </h1>
          {pageTitle && (
            <>
              <div className="h-8 w-px bg-border-light" />
              <div
                key={location.pathname}
                className="nexus-row-slide-in flex flex-col leading-tight"
              >
                <span className="text-base text-text-primary font-semibold">{pageTitle}</span>
                {(pageDescription || headerBadge) && (
                  <div className="flex items-center gap-2 mt-0.5">
                    {pageDescription && (
                      <span className="text-xs text-text-secondary">{pageDescription}</span>
                    )}
                    {headerBadge && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${
                          HEADER_BADGE_VARIANTS[headerBadge.variant] || HEADER_BADGE_VARIANTS.default
                        }`}
                      >
                        {headerBadge.label}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <UserMenu user={user} profile={profile} onLogout={handleLogout} />
      </header>

      {/* メインコンテンツ (左 = w-16 + 余白 24px、上 = h-16 + 余白 24px) */}
      <main className="pl-[88px] pt-[88px] min-h-screen relative z-10">
        <div className="p-6">
          <Outlet context={{ setHeaderBadge }} />
        </div>
      </main>
    </div>
  );
}
